import React, { forwardRef, useEffect, useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import ReactDOM from "react-dom";
import { useSelector } from "react-redux";
import styled, { css } from "styled-components";
import Slider from "./Slider";
import { PORTAL_ID } from "../constants";
import { STATIC_PATH } from "../constants/config";
import Typography from "./Typography";
import vmTheme from "../constants/theme";
import { font } from "../constants/font";

const OFFSET = 16.5;
const OptionWrapper = styled.div.attrs((props) => {
  return {
    style: {
      top: props.position.y,
      left: props.position.x,
    },
  };
})`
  width: ${(props) => props.width};
  position: absolute;
  z-index: 11;
  background: ${(props) => props.theme.btnBgLightColor} 0% 0% no-repeat
    padding-box;
  box-shadow: 0px 0px 4px ${(props) => props.theme.rgbaBlack};
  border: 1px solid ${(props) => props.theme.veryLightGray};
  border-radius: 4px;
  overflow: hidden;

  ${(props) => props.isMiniSmartFocus && `
    border-radius: 12px;
  `}

`;

const Option = styled.div`
  width: ${(props) => props.width};
  height: ${(props) => props.height || "auto"};
  min-height: 36px;
  padding: 8px 12px;
  background: ${(props) => props.theme.polarColor} 0% 0% no-repeat padding-box;
  cursor: pointer;
  &:hover {
    background: ${(props) => props.theme.btnBgLightColor};
  }
  ${(props) =>
    props.isEnableBorder &&
    css`
      border-top: 1px solid ${(props) => props.theme.primaryBorderColor};
    `}
  ${(props) =>
    props.isClose &&
    css`
      ::before {
        content: "";
        background-image: url(${STATIC_PATH}close-icon.svg);
        height: 20px;
        width: 20px;
        position: absolute;
        background-size: cover;
      }
    `}
    ${(props) => props.isMiniSmartFocus && `
       display: flex;
       justify-content: center;
       align-items: center;
       border-radius: 12px;
       gap: 14px;
    `}
`;

const AudioContainer = styled.div`
  display: flex;
  align-items: center;
  max-width: 150px;
  max-height: 36px;
  padding: 8px;
  border-top: 1px solid ${(props) => props.theme.primaryBorderColor};
  .slider-root {
    margin-top: 7px;
  }
  img {
    height: 22px;
    width: 22px;
    cursor: pointer;
  }
`;

const ContextMenu = ({ options, positionEvent, width, closeContextMenu, isMiniSmartFocus, height, src }, ref) => {
  const optionRef = useRef(null);
  const theme = useSelector((state) => state.app.get('theme'));

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isMute, setIsMute] = useState(false);

  useLayoutEffect(() => {
    if (optionRef.current && positionEvent) {
      const optionBound = optionRef.current.getBoundingClientRect();
      setPosition({
        x:
          positionEvent.x -
          (positionEvent.eventType === "contextmenu"
            ? 0
            : optionBound.width / 2 - OFFSET),
        y: positionEvent.y - optionBound.height,
      });
    }
  }, [positionEvent]);

  useEffect(() => {
    if (options[options.length - 1].actionName === "VOLUME") {
      setIsMute(options[options.length - 1].defaultVolume === 0);
    }
  }, [options]);

  const handleOutsideClick = (e) => {
    const preventClosing =
      optionRef.current && optionRef.current.contains(e.target);
    if (!preventClosing && e.target.id !== "context-menu-icon") {
      closeContextMenu();
    }
  };

  useEffect(() => {
    window.addEventListener("pointerdown", handleOutsideClick);

    return () => {
      window.removeEventListener("pointerdown", handleOutsideClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const assignRef = (r) => {
    optionRef.current = r;
    if (typeof ref === "function") {
      ref(r);
    } else if (ref && typeof ref === "object") {
      ref.current = r;
    }
  };

  return ReactDOM.createPortal(
    <>
      <OptionWrapper
        className="contextMenu-tool-bar"
        ref={assignRef}
        position={position}
        width={width}
        isMiniSmartFocus={isMiniSmartFocus}
      >
        {options.map(
          ({
            title,
            clickHandler,
            changeHandler,
            actionName,
            defaultVolume,
            isEnableBorder,
            isClose = false
          }) => {
            return title ? (
              <Option
                key={title}
                onClick={() => clickHandler(actionName)}
                width={width}
                isEnableBorder={isEnableBorder || title === "Delete"}
                isClose={isClose}
                height={height}
                isMiniSmartFocus={isMiniSmartFocus}
              >
                {
                  isMiniSmartFocus && <img alt={"delete"} src={src} />
                }
                <Typography
                  innerContent={title}
                  color={vmTheme[theme].tertiaryColor}
                  font={font.normalBase_21}
                  padding={isClose ? "0px 0px 0px 25px" : "0px"}
                  cursor="pointer"
                />
              </Option>
            ) : (
              <AudioContainer key={actionName}>
                <Slider
                  height="16px"
                  onMouseUp={(volume) => {
                    changeHandler(volume);
                    setIsMute(volume === 0);
                  }}
                  defaultValue={defaultVolume}
                  step="0.01"
                  max={1}
                  iconSrc={`${STATIC_PATH}${isMute || defaultVolume === 0 ? "mute.svg" : "un-mute.svg"}`}
                  iconValue={isMute ? 1 : 0}
                  onIconAction={() => {
                    setIsMute(!isMute);
                    changeHandler(!isMute ? 0 : 1);
                  }}
                />
              </AudioContainer>
            );
          }
        )}
      </OptionWrapper>
      <div className="backdrop" />
    </>,
    document.getElementById(PORTAL_ID)
  );
};

ContextMenu.propTypes = {
  options: PropTypes.array,
  positionEvent: PropTypes.object,
  slider: PropTypes.object,
  width: PropTypes.string,
  closeContextMenu: PropTypes.func,
  isMiniSmartFocus: PropTypes.bool,
  height: PropTypes.string,
  src: PropTypes.string,
};

export default forwardRef(ContextMenu);
